/**
 * Document handler utilities
 * Provides consistent document download and view functionality
 */

/**
 * Opens a document in a new browser tab
 * @param url - Document URL to view
 * @param fallbackMessage - Optional message to display if URL is invalid
 * @example
 * handleViewDocument('https://example.com/document.pdf')
 * handleViewDocument(document.fileViewUrl)
 */
export const handleViewDocument = (
  url?: string | null,
  fallbackMessage?: string
): void => {
  if (!url) {
    if (fallbackMessage) {
      console.warn(fallbackMessage);
    } else {
      console.warn('Nenhuma URL de visualização disponível para este documento.');
    }
    return;
  }

  try {
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error('Erro ao abrir documento:', error);
  }
};

/**
 * Downloads a document by creating a temporary anchor element
 * @param url - Document URL to download
 * @param fileName - Optional custom file name for the download
 * @param fallbackMessage - Optional message to display if URL is invalid
 * @example
 * handleDownloadDocument('https://example.com/document.pdf', 'relatorio.pdf')
 * handleDownloadDocument(document.fileDownloadUrl, document.name)
 */
export const handleDownloadDocument = (
  url?: string | null,
  fileName?: string | null,
  fallbackMessage?: string
): void => {
  if (!url) {
    if (fallbackMessage) {
      console.warn(fallbackMessage);
    } else {
      console.warn('Nenhuma URL de download disponível para este documento.');
    }
    return;
  }

  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'documento.pdf';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Erro ao fazer download do documento:', error);
  }
};

/**
 * Gets the file extension from a URL or filename
 * @param urlOrFileName - URL or filename string
 * @returns File extension (lowercase, without dot) or empty string
 * @example
 * getFileExtension('document.pdf') // Returns 'pdf'
 * getFileExtension('https://example.com/file.DOCX') // Returns 'docx'
 * getFileExtension('noextension') // Returns ''
 */
export const getFileExtension = (urlOrFileName?: string | null): string => {
  if (!urlOrFileName) return '';

  try {
    // Extract filename from URL if necessary
    const fileName = urlOrFileName.split('/').pop() || '';

    // Get extension
    const parts = fileName.split('.');
    if (parts.length < 2) return '';

    return parts.pop()?.toLowerCase() || '';
  } catch {
    return '';
  }
};

/**
 * Checks if a file is a PDF based on URL or filename
 * @param urlOrFileName - URL or filename string
 * @returns true if file is a PDF
 */
export const isPDF = (urlOrFileName?: string | null): boolean => {
  return getFileExtension(urlOrFileName) === 'pdf';
};

/**
 * Checks if a file is an image based on URL or filename
 * @param urlOrFileName - URL or filename string
 * @returns true if file is an image (jpg, jpeg, png, gif, svg, webp)
 */
export const isImage = (urlOrFileName?: string | null): boolean => {
  const extension = getFileExtension(urlOrFileName);
  return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(extension);
};

/**
 * Checks if a file is a document based on URL or filename
 * @param urlOrFileName - URL or filename string
 * @returns true if file is a document (pdf, doc, docx, xls, xlsx, txt, etc.)
 */
export const isDocument = (urlOrFileName?: string | null): boolean => {
  const extension = getFileExtension(urlOrFileName);
  return [
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'txt',
    'rtf',
    'odt',
    'ods',
    'odp',
  ].includes(extension);
};

/**
 * Gets a human-readable file type label
 * @param urlOrFileName - URL or filename string
 * @returns Human-readable file type (e.g., 'PDF', 'Imagem', 'Documento', 'Arquivo')
 */
export const getFileTypeLabel = (urlOrFileName?: string | null): string => {
  if (!urlOrFileName) return 'Arquivo';

  const extension = getFileExtension(urlOrFileName);

  if (!extension) return 'Arquivo';

  // Map extensions to labels
  const typeMap: { [key: string]: string } = {
    pdf: 'PDF',
    doc: 'Word',
    docx: 'Word',
    xls: 'Excel',
    xlsx: 'Excel',
    ppt: 'PowerPoint',
    pptx: 'PowerPoint',
    txt: 'Texto',
    rtf: 'Texto',
    jpg: 'Imagem',
    jpeg: 'Imagem',
    png: 'Imagem',
    gif: 'Imagem',
    svg: 'Imagem',
    webp: 'Imagem',
    bmp: 'Imagem',
    zip: 'Arquivo Compactado',
    rar: 'Arquivo Compactado',
    '7z': 'Arquivo Compactado',
    csv: 'Planilha CSV',
  };

  return typeMap[extension] || extension.toUpperCase();
};

/**
 * Formats file size from bytes to human-readable format
 * @param bytes - File size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted file size (e.g., '1.23 MB')
 */
export const formatFileSize = (bytes?: number | null, decimals: number = 2): string => {
  if (!bytes || bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

/**
 * Opens a URL in a new tab (generic external link handler)
 * @param url - URL to open
 * @example
 * openExternalLink('https://example.com')
 */
export const openExternalLink = (url?: string | null): void => {
  if (!url) {
    console.warn('Nenhuma URL fornecida.');
    return;
  }

  try {
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error('Erro ao abrir link externo:', error);
  }
};

/**
 * Validates if a string is a valid URL
 * @param url - String to validate
 * @returns true if valid URL
 */
export const isValidURL = (url?: string | null): boolean => {
  if (!url) return false;

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Ensures a URL is absolute by prepending https:// when no protocol is present.
 * Without this, a link like "www.example.com" is treated by the browser as a
 * path relative to the current page instead of an external address.
 * @param url - URL to normalize
 * @returns Absolute URL, or empty string if none provided
 * @example
 * ensureAbsoluteUrl('www.example.com') // Returns 'https://www.example.com'
 * ensureAbsoluteUrl('https://example.com') // Returns 'https://example.com'
 */
export const ensureAbsoluteUrl = (url?: string | null): string => {
  if (!url) return '';

  const trimmed = url.trim();
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) return trimmed;

  return `https://${trimmed}`;
};
